import motion from "@/lib/lazy-motion-tags";
import { Button } from "@/components/ui/button";
import IconLoader from "@/components/IconLoader";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import heroJpg from "@/assets/hero.jpg";
import heroWebp from "@/assets/hero.jpg.webp";
import heroAvif from "@/assets/hero.jpg.avif";
import ResponsiveImage from "@/components/ResponsiveImage";
import { trackPhoneCall } from "@/lib/analytics";

const HeroSection = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = heroJpg;
    try { link.setAttribute('importance', 'high'); } catch (e) { void e; }
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <ResponsiveImage fill avif={heroAvif} webp={heroWebp} src={heroJpg} alt="Hero background" loading="eager" />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-bl from-background/90 via-muted/70 to-background/15" />

      <div className="relative z-10 container mx-auto px-4 py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight">
              افتح بابك على{" "}
              <span className="text-gradient">جودة لا تقارن</span>
            </h1>
          </motion.div>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-muted-foreground mb-6 font-medium"
          >
            حلول أبواب WPC مبتكرة وعصرية تجمع بين المتانة، الجمال، والاستدامة
          </motion.p>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-base md:text-lg text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed"
          >
            في New Business، نقدم لك تقنية الخشب البلاستيكي المركب (WPC) التي تعيد تعريف معايير الجودة والأناقة في عالم الأبواب الداخلية. منتجاتنا مصممة لتتحمل أقسى الظروف مع الحفاظ على مظهرها الفاخر لسنوات طويلة.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <Button asChild size="lg" className="bg-gradient-primary text-primary-foreground font-bold text-lg px-8 py-6 shadow-glow hover:shadow-elegant transition-all">
              <Link to="/contact">
                اطلب عرض سعر الآن
                  <IconLoader name="ArrowLeft" className="mr-2 h-5 w-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-bold text-lg px-8 py-6 transition-colors duration-300">
              <a href="tel:+966552351116" rel="noopener noreferrer" onClick={() => trackPhoneCall("hero_section")}>
                <IconLoader name="Phone" className="ml-2 h-5 w-5" />
                احجز استشارة مجانية
              </a>
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {[
              { number: "+500", label: "مشروع منجز" },
              { number: "5", label: "سنوات خبرة" },
              { number: "100%", label: "رضا العملاء" },
              { number: "100%", label: "ضمان الجودة" },
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-gradient mb-2">{stat.number}</div>
                <div className="text-sm md:text-base text-muted-foreground">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 border-primary rounded-full flex items-start justify-center p-2"
        >
          <motion.div className="w-1 h-2 bg-primary rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
